import type { PitchExerciseType, PitchSessionStats } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './pitch-training-summary.module.css'

/**
 * End-of-session summary.
 *
 * Where Ear Training shows `correct / total`, this shows a mean: a continuous measurement has no
 * "correct". Items nobody sang are excluded from the mean rather than counted as zero, and when
 * that happened the count is shown — a percentage of seven takes is not a percentage of ten, and
 * quietly presenting it as one would flatter a broken microphone.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §6
 */

const EXERCISE_LABEL: Record<PitchExerciseType, string> = {
  tone: 'Match a tone',
  interval: 'Sing an interval',
  vocalise: 'Vocalise',
  phrase: 'Short phrase',
}

export interface PitchTrainingSummaryProps {
  exerciseType: PitchExerciseType
  stats: PitchSessionStats
  onRestart: () => void
  onSwitchExercise: (last: PitchExerciseType) => void
}

export const PitchTrainingSummary: FC<PitchTrainingSummaryProps> = props => {
  const { exerciseType, stats, onRestart, onSwitchExercise } = props
  return (
    <section className={styles.summary} aria-label="Session complete">
      <h2 className={styles.title}>Session complete</h2>
      <p className={styles.exercise}>{EXERCISE_LABEL[exerciseType]}</p>
      <p className={styles.score}>{stats.meanPercent}%</p>
      {stats.scored < stats.total ? (
        <p className={styles.scored}>
          Scored on {stats.scored} of {stats.total} — the rest were not heard.
        </p>
      ) : null}
      <div className={styles.actions}>
        <Button variant="contained" color="primary" size="large" onClick={onRestart}>
          Start new session
        </Button>
        <Button variant="outlined" size="large" onClick={() => onSwitchExercise(exerciseType)}>
          Try another exercise
        </Button>
      </div>
    </section>
  )
}
